import type { Node as PMNode } from '@tiptap/pm/model';
import type { EditorView } from '@tiptap/pm/view';
import DOMPurify from 'dompurify';

/**
 * Sanitize raw HTML before it's rendered into the editor. Pasted/imported HTML is untrusted, so we
 * run it through DOMPurify (audit S4). The previous hand-rolled pass only blocked `javascript:` and
 * missed `data:text/html`, `vbscript:`, style-based vectors, and namespace/mXSS tricks — DOMPurify's
 * default policy already blocks all of those (only http(s)/mailto/tel-style URIs survive) while
 * keeping the structural/presentational markup (tables, divs, images) this node exists to display.
 * FORBID_TAGS mirrors the previous explicit denylist as defense-in-depth.
 */
const FORBIDDEN_TAGS = ['script', 'style', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form'];

export function sanitizeHtml(raw: string): string {
  return DOMPurify.sanitize(raw, { FORBID_TAGS: FORBIDDEN_TAGS });
}

interface HtmlNodeViewProps {
  node: PMNode;
  editor: { view: EditorView };
  getPos: () => number | undefined;
}

/**
 * Node view for the `html` block. The node is an atom that stores its source in the `html` attr;
 * by default we show the sanitized HTML rendered (read-only, Logseq-style). Double-clicking it (or
 * the "edit" button) opens the raw source in a textarea with Done/Cancel; Save (Done, blur, or
 * ⌘/Ctrl-Enter) writes the new source back to the node, Esc/Cancel discards.
 *
 * Closing always hides the editor FIRST and only then attempts the save, so a failed dispatch can
 * never strand the editor open. While editing, the node view owns all events (`stopEvent`) so the
 * textarea and toolbar work without ProseMirror intercepting keys/clicks.
 */
export function htmlNodeView(props: HtmlNodeViewProps): {
  dom: HTMLElement;
  update: (node: PMNode) => boolean;
  stopEvent: () => boolean;
  ignoreMutation: () => boolean;
} {
  let html = props.node.attrs.html as string;
  let editing = false;

  const dom = document.createElement('div');
  dom.className = 'raw-html';

  const editBtn = document.createElement('button');
  editBtn.type = 'button';
  editBtn.className = 'raw-html-edit';
  editBtn.textContent = '</> edit';

  const preview = document.createElement('div');
  preview.className = 'raw-html-preview';
  preview.contentEditable = 'false';

  const source = document.createElement('textarea');
  source.className = 'raw-html-source';
  source.spellcheck = false;
  source.hidden = true;

  const toolbar = document.createElement('div');
  toolbar.className = 'raw-html-toolbar';
  toolbar.hidden = true;
  const doneBtn = document.createElement('button');
  doneBtn.type = 'button';
  doneBtn.className = 'raw-html-done';
  doneBtn.textContent = 'Done';
  const cancelBtn = document.createElement('button');
  cancelBtn.type = 'button';
  cancelBtn.className = 'raw-html-cancel';
  cancelBtn.textContent = 'Cancel';
  toolbar.append(doneBtn, cancelBtn);

  const renderPreview = (): void => {
    preview.innerHTML = sanitizeHtml(html);
  };

  const open = (): void => {
    editing = true;
    source.value = html;
    source.hidden = false;
    toolbar.hidden = false;
    preview.hidden = true;
    editBtn.hidden = true;
    source.focus();
  };

  const close = (save: boolean): void => {
    if (!editing) return;
    const next = source.value;
    // Hide the editor FIRST — closing must never depend on the save succeeding.
    editing = false;
    source.hidden = true;
    toolbar.hidden = true;
    preview.hidden = false;
    editBtn.hidden = false;
    if (save && next !== html) {
      const pos = props.getPos();
      if (typeof pos === 'number') {
        try {
          const { view } = props.editor;
          view.dispatch(view.state.tr.setNodeMarkup(pos, undefined, { html: next }));
          html = next;
        } catch {
          /* node moved/removed mid-edit — keep the rendered preview as-is */
        }
      }
    }
    renderPreview();
  };

  editBtn.addEventListener('click', open);
  preview.addEventListener('dblclick', open);
  // Keep the textarea focused when a toolbar button is pressed (preventDefault on mousedown), so
  // the button's own handler decides save-vs-discard instead of a competing blur.
  doneBtn.addEventListener('mousedown', (e) => e.preventDefault());
  cancelBtn.addEventListener('mousedown', (e) => e.preventDefault());
  doneBtn.addEventListener('click', () => close(true));
  cancelBtn.addEventListener('click', () => close(false));
  source.addEventListener('blur', () => close(true)); // genuine click-away saves and closes
  source.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close(false);
    } else if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      close(true);
    }
    e.stopPropagation(); // keep typing/shortcuts inside the textarea, not ProseMirror
  });

  renderPreview();
  dom.append(editBtn, preview, source, toolbar);

  return {
    dom,
    update(updated) {
      if (updated.type.name !== 'html') return false;
      html = updated.attrs.html as string;
      if (!editing) renderPreview(); // don't clobber an in-progress edit
      return true;
    },
    // While editing, the node view owns every event so the textarea/toolbar work; in preview mode
    // ProseMirror handles clicks (so the block stays selectable/deletable).
    stopEvent: () => editing,
    ignoreMutation: () => true,
  };
}
